import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ExclamationCircleIcon from '~/components/Icons/svgs/ExclamationCircleIcon'

import { BlockContent } from '~/bootcamp/Activities/types/activities.types'

import {
  OrderElementsAnswerType,
  OrderElementsActivityData,
  OrderElementsProps,
  TaskStatus,
} from './types/OrderElements.types'
import { generateOrderElementsActivityData, sortData } from './types/OrderElements.data'
import OrderElementsApp from './app/OrderElementsApp'

function OrderElements({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onCurrentQuestionChange,
  actionButtonsFC,
  questions,
}: OrderElementsProps): React.ReactElement {
  const [answer, setAnswer] = useState<boolean[]>([])
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [question, setQuestion] = useState<BlockContent[]>([])
  const [dataSorted, setDataSorted] = useState<boolean>(false)
  const [taskStatus, setTaskStatus] = useState<TaskStatus>(TaskStatus.Unchecked)

  const [feedback, setFeedback] = useState<OrderElementsAnswerType | null>(null)

  const { t } = useTranslation('bootcamp')

  const [activityData, setActivityData] = useState<OrderElementsActivityData>({
    question: {
      questionId: '',
      _key: '',
      elements: [],
      question: [],
    },
    questionIndex: 0,
  })

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEffect(() => {
    setAnswer([])
    setActivityData(generateOrderElementsActivityData(questions, questionHistory))
    setFeedback(null)
  }, [questionHistory, questions])

  // Keyed on the questionId string: the effect above returns a fresh activityData object every run.
  useEffect(() => {
    if (activityData.question.questionId) onCurrentQuestionChange?.(activityData.question.questionId)
  }, [activityData.question.questionId, onCurrentQuestionChange])

  useEffect(() => {
    if (activityData.question.questionId !== '' && activityData.question.questionId !== '') {
      sortData(activityData.question)
      setDataSorted(true)
      setQuestion(activityData.question.question)
    }
  }, [activityData])

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    setTaskStatus(TaskStatus.Reset)
    setDataSorted(false)
    setActivityData({
      question: {
        question: [],
        questionId: '',
        _key: '',
        elements: { elements: [] },
      },
      questionIndex: 0,
    })
    onNextQuestionClick()
  }

  const getFeedBack = (feedbackKey: string | null): React.ReactElement => {
    let message = ''
    switch (feedbackKey) {
      case OrderElementsAnswerType.SELECT_ALL_CORRECT:
        message = t('feedback.select-all-correct')
        break
      case OrderElementsAnswerType.SELECT_CORRECT:
        message = t('feedback.select-correct')
        break
      case OrderElementsAnswerType.SELECT_ONLY_CORRECT:
        message = t('feedback.select-only-correct')
        break
      default:
        return <></>
    }
    return (
      <div className="w-full min-h-9 mb-2">
        {message.length > 0 && (
          <div className="flex items-center p-2 bg-purple text-white text-xs md:text-sm leading-4 font-normal text-center rounded-medium">
            <div className="mr-2">
              <span className="shadow-large">
                <SVGIcon icon={ExclamationCircleIcon} pathClassName="text-white" size={19} />
              </span>
            </div>
            {message}
          </div>
        )}
      </div>
    )
  }

  const showFeedbackMessage = useCallback(
    (responseFeedback: OrderElementsAnswerType | null): void => {
      setFeedback(responseFeedback)
      if (responseFeedback === null) return
      // A wrong order arrives as SELECT_ALL_CORRECT, so anything but CORRECT is wrong.
      onAnswerStateChange(responseFeedback === OrderElementsAnswerType.CORRECT, activityData.question.questionId)
    },
    [onAnswerStateChange, setFeedback, activityData.question.questionId],
  )

  function ClearForm(): void {
    setTaskStatus(TaskStatus.Reset)
    setAnswer([])
    setFeedback(null)
  }

  return (
    <ActivityWrapper>
      <div className="my-auto w-full justify-center items-center text-purple font-semibold text-md">
        {getFeedBack(feedback)}
        {dataSorted && (
          <OrderElementsApp
            question={question}
            setFeedBackMessage={showFeedbackMessage}
            setTaskStatus={setTaskStatus}
            taskStatus={taskStatus}
          />
        )}
      </div>

      {actionButtonsFC &&
        actionButtonsFC({
          formValid: taskStatus === TaskStatus.ReadyToCheck || taskStatus === TaskStatus.Wrong,
          correctAnswer: answerState ?? false,
          submitAnswerAction: () => {
            setTaskStatus(TaskStatus.Checking)
          },
          clearFormAction: () => {
            ClearForm()
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default OrderElements
